/**
 * Canonical Australian mobile-number handling for the eResults UI.
 *
 * Single source of truth so every form (Account Settings, Notification Settings,
 * eOrder, MFA enrolment, ...) treats mobile numbers identically. Previously each
 * screen had its own rule, which drifted apart and let invalid numbers through
 * (see docs/known-bugs.md — KB-003).
 *
 * Display vs storage:
 *   - DISPLAY as "0400 000 000" (grouped 4-3-3 with single spaces)
 *   - STORE as "0400000000" (digits only)
 *
 * Validation is done on the DIGITS (spacing is presentational and applied
 * automatically for display), so a number is valid when it is exactly 10 digits
 * starting with the AU mobile prefix "04".
 */

// Valid stored form: exactly 10 digits, AU mobile prefix "04".
export const MOBILE_NUMBER_DIGITS_REGEX = /^04[0-9]{8}$/;

export const MOBILE_NUMBER_FORMAT_MESSAGE = "Required format is 0400 000 000";

/** Strip everything except digits — this is the value stored in the DB. */
export const toMobileDigits = (value?: string | null): string => {
  return value ? value.replace(/[^0-9]/g, "") : "";
};

/**
 * Format digits (or a partially-typed value) for display as "0400 000 000".
 * Groups the first 10 digits 4-3-3. Any digits beyond 10 are preserved (appended)
 * so over-length input still shows what the user typed and fails validation rather
 * than being silently trimmed to a valid-looking number.
 */
export const formatMobileNumber = (value?: string | null): string => {
  const digits = toMobileDigits(value);
  if (!digits) {
    return "";
  }

  const head = digits.slice(0, 10);
  const overflow = digits.slice(10);

  let formatted = head;
  if (head.length > 7) {
    formatted = `${head.slice(0, 4)} ${head.slice(4, 7)} ${head.slice(7)}`;
  } else if (head.length > 4) {
    formatted = `${head.slice(0, 4)} ${head.slice(4)}`;
  }

  return overflow ? `${formatted}${overflow}` : formatted;
};

/**
 * True when `value` is a valid AU mobile (10 digits, "04" prefix). Spaces/formatting
 * are ignored — validation is on the digits. Empty/undefined returns false; callers
 * that allow an optional mobile should use `isValidOptionalMobileNumber`.
 */
export const isValidMobileNumber = (value?: string | null): boolean => {
  return MOBILE_NUMBER_DIGITS_REGEX.test(toMobileDigits(value));
};

/**
 * Yup `.test()` predicate for an OPTIONAL mobile field: empty passes, otherwise the
 * value (ignoring spaces) must be a valid AU mobile. Use with:
 *   yup.string().test("Mobile Test", MOBILE_NUMBER_FORMAT_MESSAGE, isValidOptionalMobileNumber)
 */
export const isValidOptionalMobileNumber = (value?: string | null): boolean => {
  return value && value.length ? isValidMobileNumber(value) : true;
};
